import { ErrorResponse, IPaymentIntent, Terminal } from '@stripe/terminal-js'
import { fetchApi, trackUserEvent } from '@vend/utilities'
import { ApiTransaction } from '@vend/types'
import { loadStripe, PaymentIntent } from '@stripe/stripe-js'
import { TerminalEventMonitoring } from '@lightspeed-payments/payments-terminal-sdk'
import { TimerStarterFunction } from '../hooks/timeTracking'
import { WEB_REGISTER } from '../../../web-register.constant'
import { isErrorResponse, isErrorWithIntent } from './errorChecking'
import {
  LSPAYCaptureOptions,
  LSPAYConfirmOptions,
  LSPAYPaymentOptions,
  SwiperError,
  SwiperErrorResponse,
} from './lspay.types'
import {
  TRANSACTION_TYPE,
  LSPAY_PRODUCT_VERSION,
  LSPAY_STEPS,
} from './lspayConstants'
import { withRetry } from './withRetry'
import { fetchCNPConfig } from './fetchCNPConfig'
import { trackTerminalCompleteWithError } from './terminalTracking'

// Create a payment intent through the backend,
// that we will then use to collect, process, capture a payment
export const createPaymentIntent = async ({
  amount,
  currency,
  retailerPaymentTypeId,
  registerId,
  outletId,
  metadata,
}: Omit<
  LSPAYPaymentOptions,
  'transactionId' | 'originalRegisterSalePaymentId'
>) => {
  const response = await fetchApi(
    `/api/2.0/payment_types/${retailerPaymentTypeId}/lspay/intent/${registerId}`,
    {
      method: 'POST',
      body: JSON.stringify({
        amount: amount.toNumber(),
        currency,
        outlet_id: outletId,
        // we want Interac support, let the backend decide if that's possible (i.e. only for Canada)
        support_interac: true,
        metadata: {
          ...metadata,
          product_version: LSPAY_PRODUCT_VERSION,
        },
      }),
    }
  )

  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }

  return (await response.json()).client_secret as string
}

// Collect then process a payment (does not include capturing it, unless we use auto-capture as in AU).
// Returns a promise of IPaymentIntent from Stripe Terminal in most cases,
// or "classic Stripe" PaymentIntent if we recovered it after a network issue.
export const collectPayment = async (
  clientSecret: string,
  outletId: string,
  terminal: Terminal,
  terminalMonitoringInstance: TerminalEventMonitoring,
  trackTiming: TimerStarterFunction,
  isRetry: boolean
): Promise<IPaymentIntent | PaymentIntent> => {
  if (isRetry) {
    // Not updating to the use the new property due to neglibile volume of usage
    terminalMonitoringInstance.sendEvent('START', {
      transactionType: TRANSACTION_TYPE.PAYMENT,
    })
  }
  const stopCollectTimer = trackTiming(LSPAY_STEPS.COLLECT)
  try {
    const collectedPayment = await terminal.collectPaymentMethod(clientSecret)
    stopCollectTimer()

    if (isErrorResponse(collectedPayment)) {
      const { error } = collectedPayment as ErrorResponse
      throw error
    }

    const stopProcessTimer = trackTiming(LSPAY_STEPS.PROCESS)
    const processedPayment = await terminal.processPayment(
      collectedPayment.paymentIntent
    )
    stopProcessTimer()

    if (isErrorResponse(processedPayment)) {
      const { error } = processedPayment as ErrorResponse
      throw error
    }

    trackTerminalCompleteWithPaymentIntent(
      terminalMonitoringInstance,
      processedPayment.paymentIntent
    )
    return processedPayment.paymentIntent
  } catch (error: any) {
    trackTerminalCompleteWithError(terminalMonitoringInstance, error)
    // Sometimes an error is hiding a successful payment !
    if (isErrorWithIntent(error)) {
      if (hasPaymentProcessingSucceeded(error.payment_intent.status)) {
        // The collection actually worked.
        // This happens when we retry an intent that was already collected (but a network issue prevented the app from knowing on the 1st attempt).
        // Recover, so we avoid an infinite loop of errors as long as the retailer retries
        // (and worse for auto-capture intents: we fail to let them know the payment went through, leading to double charges).
        // The app and Swiper will handle the rest of the flow whether it needs capturing or not.
        return error.payment_intent
      }
      // there is an intent but it failed to process
      throw error
    }

    if (error?.code !== 'canceled') {
      // when there's no intent in the response, it's likely we've had a connection issue talking to the terminal/Stripe.
      // check the payment intent status via a call to Stripe as it may have been processed.
      trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Retrieving LSPay intent', {
        intent: extractFromSecret(clientSecret),
        errorCode: error?.code ?? '',
        errorMessage: error?.message ?? '',
      })
      const intent = await withRetry(
        () => retrievePaymentIntent(outletId, clientSecret),
        'retrievePaymentIntent',
        2
      )
      if (hasPaymentProcessingSucceeded(intent.status)) {
        return intent
      }
    }
    throw error
  }
}

// Retrieve the payment intent information from the secret, so we can check its status
export const retrievePaymentIntent = async (
  outletId: string,
  clientSecret: string
): Promise<PaymentIntent> => {
  const config = await fetchCNPConfig(outletId)
  const stripe = await loadStripe(config.client_key)
  // stripe can only be `null` when this runs on server, so it's not possible here
  const { error, paymentIntent } = await stripe!.retrievePaymentIntent(
    clientSecret
  )
  if (error) {
    throw error
  }
  if (!paymentIntent) {
    throw new Error('Stripe unexpectedly gave no error and no paymentIntent')
  }
  // Stripe gives either an error or the intent, so we are now sure to get an intent
  return paymentIntent
}

// Capture a payment through the backend
const capturePaymentOperation = async ({
  transactionId,
  registerId,
  retailerPaymentTypeId,
  data,
}: LSPAYCaptureOptions): Promise<ApiTransaction> => {
  const response = await fetchApi(
    `/api/2.0/registers/${registerId}/transactions/${retailerPaymentTypeId}/payment/${transactionId}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        ...data,
        amount: data.amount.toNumber(),
      }),
    }
  )

  if (response.status < 200 || response.status > 299) {
    throw response
  }

  return response.json()
}

/**
 * Capture a payment at X-series backend with retries.
 * For payments that has been captured already, calling the same endpoint for payment capture will not attempt to capture,
 * and instead only persist the payment to X-Series DB.
 */
export const capturePayment = async ({
  transactionId,
  registerId,
  retailerPaymentTypeId,
  data,
}: LSPAYCaptureOptions): Promise<ApiTransaction> =>
  await withRetry<ApiTransaction>(
    () =>
      capturePaymentOperation({
        transactionId,
        registerId,
        retailerPaymentTypeId,
        data,
      }),
    'capturePayment'
  )

// Returns the payment method type for the given intent, or 'unknown' if that information is missing.
// Note `PaymentIntent` is missing the charges. This is what we receive when retrieving the intent from
// Stripe.js (not Stripe Terminal) after an error. In that case the payment method will always be unknown.
// Swiper will verify whether the intent needs capturing based on this.
export const getPaymentMethodType = (
  intent: IPaymentIntent | PaymentIntent
): string => {
  if ('charges' in intent) {
    const method = intent.charges.data[0]?.payment_method_details?.type
    if (method) {
      return method
    }
  }
  return 'unknown'
}

// Returns the intent part of a secret that is in the form `pi_..._secret_...`
export const extractFromSecret = (secret: string) => secret.split('_secret_')[0]

// Has the payment actually already been captured; or collected and not needing a capture (debit such as eftpos, interac)
export const hasPaymentGoneThrough = (intentStatus: IPaymentIntent['status']) =>
  // checking against a string is safe because the Stripe Status type is a union of the valid strings
  intentStatus === 'succeeded'

// Has the payment been processed succesfully (so it either needs to be captured or it succeeded = it's done)
const hasPaymentProcessingSucceeded = (
  intentStatus: IPaymentIntent['status']
) =>
  // checking against a string is safe because the Stripe Status type is a union of the valid strings
  intentStatus === 'succeeded' || intentStatus === 'requires_capture'

const trackTerminalCompleteWithPaymentIntent = (
  terminalMonitoringInstance: TerminalEventMonitoring,
  paymentIntent: { status: PaymentIntent.Status }
) => {
  // From stripe docs: If the payment attempt fails (for example due to a decline), the PaymentIntent’s status returns to requires_payment_method.
  if (paymentIntent.status === 'requires_payment_method') {
    terminalMonitoringInstance.sendEvent('COMPLETE', {
      payment: {
        status: 'DECLINED',
      },
      transactionType: TRANSACTION_TYPE.PAYMENT,
    })
    return
  }
  if (hasPaymentProcessingSucceeded(paymentIntent.status)) {
    terminalMonitoringInstance.sendEvent('COMPLETE', {
      payment: {
        status: 'APPROVED',
      },
      transactionType: TRANSACTION_TYPE.PAYMENT,
    })
    return
  }
  if (paymentIntent.status === 'canceled') {
    terminalMonitoringInstance.sendEvent('COMPLETE', {
      payment: {
        status: 'CANCELLED',
      },
      transactionType: TRANSACTION_TYPE.PAYMENT,
    })
    return
  }

  terminalMonitoringInstance.sendEvent('COMPLETE', {
    payment: {
      status: 'ERROR',
    },
    transactionType: TRANSACTION_TYPE.PAYMENT,
  })
}

// Confirm a payment through the backend
// we didn't implement retry here because a PaymentMethod could not be used more than once
// just in case Swiper has confirmed the payment method already
export const confirmPayment = async ({
  registerId,
  intentId,
  paymentMethodId,
}: LSPAYConfirmOptions): Promise<any> => {
  const response = await fetchApi(
    `/api/2.0/lspay/registers/${registerId}/intent/confirm`,
    {
      method: 'POST',
      body: JSON.stringify({
        payment_method_id: paymentMethodId,
        payment_intent_id: intentId,
        moto: true,
      }),
    }
  )

  if (response.status < 200 || response.status > 299) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }

  return response.json()
}
